import type { ChangeEventHandler, MouseEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Header,
  IconButton,
  Input,
  MenuItem,
  Scroll,
  Spinner,
  Text,
  Tooltip,
  config,
  toRem,
} from 'folds';
import { TooltipProvider } from '$components/overlay-stack';
import type { MatrixClient, Room, RoomMember } from '$types/matrix-sdk';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';

import { AvatarPresence, PresenceBadge } from '$components/presence';
import { useUserPresence } from '$hooks/useUserPresence';
import { useMatrixClient } from '$hooks/useMatrixClient';
import type { SearchItemStrGetter, UseAsyncSearchOptions } from '$hooks/useAsyncSearch';
import { useAsyncSearch } from '$hooks/useAsyncSearch';
import { useDebounce } from '$hooks/useDebounce';
import { TypingIndicator } from '$components/typing-indicator';
import { getAvatarUrl, getMemberDisplayName, getMemberSearchStr } from '$utils/room/display';
import { getMxIdLocalPart } from '$utils/matrix';
import { useSetSetting, useSetting } from '$state/hooks/settings';
import { settingsAtom } from '$state/settings';
import { useAtomValue } from 'jotai';
import { nicknamesAtom } from '$state/nicknames';
import { ScrollTopContainer } from '$components/scroll-top-container';
import { UserAvatar } from '$components/user-avatar';
import {
  ArrowsDownUp,
  CaretUp,
  chipIcon,
  composerIcon,
  Funnel,
  MagnifyingGlass,
  userFallbackIcon,
  X,
} from '$components/icons/phosphor';
import { useRoomTypingMember } from '$hooks/useRoomTypingMembers';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { useMembershipFilter, useMembershipFilterMenu } from '$hooks/useMemberFilter';
import { useMemberPowerSort, useMemberSort, useMemberSortMenu } from '$hooks/useMemberSort';
import { useGetMemberPowerLevel, usePowerLevelsContext } from '$hooks/usePowerLevels';
import { MemberMenuList } from '$components/MemberSortMenu';
import { ResponsiveMenu } from '$components/ResponsiveMenu';
import { useMenuAnchor } from '$hooks/useMenuAnchor';
import { useOpenUserRoomProfile, useUserRoomProfileState } from '$state/hooks/userRoomProfile';
import { useSpaceOptionally } from '$hooks/useSpace';
import { ContainerColor } from '$styles/ContainerColor.css';
import { useFlattenPowerTagMembers, useGetMemberPowerTag } from '$hooks/useMemberPowerTag';
import { useRoomCreators } from '$hooks/useRoomCreators';
import { useSableCosmetics } from '$hooks/useSableCosmetics';
import { formatCompactNumber } from '$utils/formatCompactNumber';
import * as css from './MembersDrawer.css';
import { SidebarResizer } from '$pages/client/sidebar/SidebarResizer';
import { useScreenSizeContext, ScreenSize } from '$hooks/useScreenSize';

type MemberDrawerHeaderProps = {
  room: Room;
  hideText?: boolean;
};
function MemberDrawerHeader({ room, hideText }: MemberDrawerHeaderProps) {
  const setPeopleDrawer = useSetSetting(settingsAtom, 'isPeopleDrawer');

  return (
    <Header className={css.MembersDrawerHeader} variant="Background" size="600">
      <Box grow="Yes" alignItems="Center" gap="200">
        {!hideText && (
          <Box grow="Yes" alignItems="Center" gap="200">
            <Text title={`${room.getJoinedMemberCount()} Members`} size="H5" truncate>
              {`${formatCompactNumber(room.getJoinedMemberCount())} Members`}
            </Text>
          </Box>
        )}
        <Box shrink="No" alignItems="Center">
          <TooltipProvider
            position="Bottom"
            align="End"
            offset={4}
            tooltip={
              <Tooltip>
                <Text>Close</Text>
              </Tooltip>
            }
          >
            {(triggerRef) => (
              <IconButton
                ref={triggerRef}
                variant="Background"
                onClick={() => setPeopleDrawer(false)}
              >
                {composerIcon(X)}
              </IconButton>
            )}
          </TooltipProvider>
        </Box>
      </Box>
    </Header>
  );
}

type MemberItemProps = {
  mx: MatrixClient;
  useAuthentication: boolean;
  room: Room;
  member: RoomMember;
  onClick: MouseEventHandler<HTMLButtonElement>;
  pressed?: boolean;
  typing?: boolean;
  hideText?: boolean;
};
function MemberItem({
  mx,
  useAuthentication,
  room,
  member,
  onClick,
  pressed,
  typing,
  hideText,
}: MemberItemProps) {
  const nicknames = useAtomValue(nicknamesAtom);
  const name =
    getMemberDisplayName(room, member.userId, nicknames) ??
    getMxIdLocalPart(member.userId) ??
    member.userId;

  // Increased the request size to 128x128 to maintain quality for the larger avatar
  const avatarMxcUrl = member.getMxcAvatarUrl();
  const avatarUrl = getAvatarUrl(mx, avatarMxcUrl, 128, useAuthentication);

  const presence = useUserPresence(member.userId);
  const { color, font } = useSableCosmetics(member.userId, room);
  const MemberAvatar = (
    <div
      style={{
        position: 'relative',
        width: toRem(40),
        height: toRem(40),
        transform: 'scale(0.85)',
        transformOrigin: 'center',
      }}
    >
      <AvatarPresence
        badge={
          presence && presence.lastActiveTs !== 0 ? (
            <PresenceBadge presence={presence.presence} size="200" />
          ) : undefined
        }
      >
        <Avatar size="300" radii="400">
          <UserAvatar
            userId={member.userId}
            src={avatarUrl ?? undefined}
            alt={name}
            renderFallback={() => userFallbackIcon('md')}
          />
        </Avatar>
      </AvatarPresence>
    </div>
  );

  if (hideText)
    return (
      <IconButton
        onClick={onClick}
        size="300"
        aria-pressed={pressed}
        data-user-id={member.userId}
        style={{ padding: '0' }}
      >
        {MemberAvatar}
      </IconButton>
    );
  return (
    <MenuItem
      style={{ padding: `0 ${config.space.S200}` }}
      aria-pressed={pressed}
      data-user-id={member.userId}
      variant="Background"
      radii="400"
      onClick={onClick}
      before={MemberAvatar}
      after={
        typing && (
          <Badge size="300" variant="Secondary" fill="Soft" radii="Pill" outlined>
            <TypingIndicator size="300" />
          </Badge>
        )
      }
    >
      <Box direction="Column" grow="Yes" gap="0">
        <Text size="T300" truncate style={{ color, fontFamily: font, lineHeight: '1.2' }}>
          {name}
        </Text>
        {presence?.status && (
          <Text
            size="T200"
            truncate
            style={{
              opacity: config.opacity.P300,
              fontFamily: font,
              marginTop: '-2px',
            }}
          >
            {presence.status}
          </Text>
        )}
      </Box>
    </MenuItem>
  );
}

const SEARCH_OPTIONS: UseAsyncSearchOptions = {
  limit: 1000,
  matchOptions: {
    contain: true,
  },
};

const mxIdToName = (mxId: string) => getMxIdLocalPart(mxId) ?? mxId;

type MembersDrawerProps = {
  room: Room;
  members: RoomMember[];
};
export function MembersDrawer({ room, members }: MembersDrawerProps) {
  const mx = useMatrixClient();
  const nicknames = useAtomValue(nicknamesAtom);
  const useAuthentication = useMediaAuthentication();
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollTopAnchorRef = useRef<HTMLDivElement>(null);
  const powerLevels = usePowerLevelsContext();
  const creators = useRoomCreators(room);
  const getPowerTag = useGetMemberPowerTag(room, creators, powerLevels);
  const getPowerLevel = useGetMemberPowerLevel(powerLevels);

  const fetchingMembers = members.length < room.getJoinedMemberCount();
  const openUserRoomProfile = useOpenUserRoomProfile();
  const space = useSpaceOptionally();
  const openProfileUserId = useUserRoomProfileState()?.userId;

  const membershipFilterMenu = useMembershipFilterMenu();
  const sortFilterMenu = useMemberSortMenu();
  const [sortFilterIndex, setSortFilterIndex] = useSetting(settingsAtom, 'memberSortFilterIndex');
  const [membershipFilterIndex, setMembershipFilterIndex] = useState(0);

  const membershipFilter = useMembershipFilter(membershipFilterIndex, membershipFilterMenu);
  const memberSort = useMemberSort(sortFilterIndex, sortFilterMenu);
  const memberPowerSort = useMemberPowerSort(creators, getPowerLevel);

  const typingMembers = useRoomTypingMember(room.roomId);

  const filteredMembers = useMemo(
    () =>
      members.filter(membershipFilter.filterFn).toSorted(memberSort.sortFn).sort(memberPowerSort),
    [members, membershipFilter, memberSort, memberPowerSort]
  );

  const getRoomMemberStr = useCallback<SearchItemStrGetter<RoomMember>>(
    (m, query) => getMemberSearchStr(m, query, mxIdToName, nicknames),
    [nicknames]
  );

  const [result, search, resetSearch] = useAsyncSearch(
    filteredMembers,
    getRoomMemberStr,
    SEARCH_OPTIONS
  );
  if (!result && searchInputRef.current?.value) search(searchInputRef.current.value);

  const processMembers = result ? result.items : filteredMembers;

  const PLTagOrRoomMember = useFlattenPowerTagMembers(processMembers, getPowerTag);

  const virtualizer = useVirtualizer({
    count: PLTagOrRoomMember.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 40,
    overscan: 10,
  });

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = useDebounce(
    useCallback(
      (evt) => {
        if (evt.target.value) search(evt.target.value);
        else resetSearch();
      },
      [search, resetSearch]
    ),
    { wait: 200 }
  );

  const handleMemberClick: MouseEventHandler<HTMLButtonElement> = (evt) => {
    const btn = evt.currentTarget as HTMLButtonElement;
    const userId = btn.getAttribute('data-user-id');
    if (!userId) return;

    const cords = btn.getBoundingClientRect();
    // BODGE, dependent on menuItem height staying at toRem(40)
    cords.y = Math.min(cords.y, window.innerHeight - 42);

    openUserRoomProfile(room.roomId, space?.roomId, userId, undefined, cords, 'Left');
  };

  const [memberSidebarWidth, setMemberSidebarWidth] = useSetting(
    settingsAtom,
    'memberSidebarWidth'
  );
  const [curWidth, setCurWidth] = useState(memberSidebarWidth);
  useEffect(() => {
    setCurWidth(memberSidebarWidth);
  }, [memberSidebarWidth]);

  const screenSize = useScreenSizeContext();
  const isMobile = screenSize === ScreenSize.Mobile;
  const hideText = curWidth <= 80 && !isMobile;
  const filterMenu = useMenuAnchor<HTMLButtonElement>();
  const sortMenu = useMenuAnchor<HTMLButtonElement>();
  return (
    <Box
      className={classNames(css.MembersDrawer, ContainerColor({ variant: 'Background' }))}
      shrink="No"
      direction="Column"
      style={{
        position: 'relative',
        width: isMobile ? '100%' : toRem(curWidth),
      }}
    >
      <MemberDrawerHeader room={room} hideText={hideText} />
      <Box className={css.MemberDrawerContentBase} grow="Yes">
        {!isMobile && (
          <SidebarResizer
            setCurWidth={setCurWidth}
            sidebarWidth={memberSidebarWidth}
            setSidebarWidth={setMemberSidebarWidth}
            instep={50}
            outstep={176}
            minValue={50}
            maxValue={350}
            isReversed
          />
        )}
        <Scroll ref={scrollRef} variant="Background" size="300" visibility="Hover" hideTrack>
          <Box className={css.MemberDrawerContent} direction="Column" gap="200">
            {!hideText && (
              <>
                <Box
                  ref={scrollTopAnchorRef}
                  className={css.DrawerGroup}
                  direction="Column"
                  gap="200"
                >
                  <Box alignItems="Center" justifyContent="SpaceBetween" gap="200">
                    <ResponsiveMenu
                      anchor={filterMenu.anchor}
                      requestClose={filterMenu.close}
                      position="Bottom"
                      align="Start"
                      offset={4}
                      menu={
                        <MemberMenuList
                          items={membershipFilterMenu}
                          selected={membershipFilterIndex}
                          onSelect={setMembershipFilterIndex}
                          requestClose={filterMenu.close}
                        />
                      }
                    >
                      <Chip
                        onClick={filterMenu.triggerProps.onClick}
                        variant="Background"
                        size="400"
                        radii="300"
                        before={chipIcon(Funnel)}
                      >
                        <Text size="T200">{membershipFilter.name}</Text>
                      </Chip>
                    </ResponsiveMenu>
                    <ResponsiveMenu
                      anchor={sortMenu.anchor}
                      requestClose={sortMenu.close}
                      position="Bottom"
                      align="End"
                      offset={4}
                      menu={
                        <MemberMenuList
                          items={sortFilterMenu}
                          selected={sortFilterIndex}
                          onSelect={setSortFilterIndex}
                          requestClose={sortMenu.close}
                        />
                      }
                    >
                      <Chip
                        onClick={sortMenu.triggerProps.onClick}
                        variant="Background"
                        size="400"
                        radii="300"
                        after={chipIcon(ArrowsDownUp)}
                      >
                        <Text size="T200">{memberSort.name}</Text>
                      </Chip>
                    </ResponsiveMenu>
                  </Box>
                  <Box direction="Column" gap="100">
                    <Input
                      ref={searchInputRef}
                      onChange={handleSearchChange}
                      style={{ paddingRight: config.space.S200 }}
                      placeholder="Type name..."
                      variant="Surface"
                      size="400"
                      radii="400"
                      before={chipIcon(MagnifyingGlass)}
                      after={
                        result && (
                          <Chip
                            variant={result.items.length > 0 ? 'Success' : 'Critical'}
                            size="400"
                            radii="Pill"
                            aria-pressed
                            onClick={() => {
                              if (searchInputRef.current) {
                                searchInputRef.current.value = '';
                                searchInputRef.current.focus();
                              }
                              resetSearch();
                            }}
                            after={chipIcon(X)}
                          >
                            <Text size="B300">{`${result.items.length || 'No'} ${
                              result.items.length === 1 ? 'Result' : 'Results'
                            }`}</Text>
                          </Chip>
                        )
                      }
                    />
                  </Box>
                </Box>

                <ScrollTopContainer scrollRef={scrollRef} anchorRef={scrollTopAnchorRef}>
                  <IconButton
                    onClick={() => virtualizer.scrollToOffset(0)}
                    variant="Surface"
                    radii="Pill"
                    outlined
                    size="300"
                    aria-label="Scroll to Top"
                  >
                    {composerIcon(CaretUp)}
                  </IconButton>
                </ScrollTopContainer>

                {!fetchingMembers && !result && processMembers.length === 0 && (
                  <Text style={{ padding: config.space.S300 }} align="Center">
                    {`No "${membershipFilter.name}" Members`}
                  </Text>
                )}
              </>
            )}
            <Box className={css.MembersGroup} direction="Column" gap="100">
              <div
                style={{
                  position: 'relative',
                  height: virtualizer.getTotalSize(),
                }}
              >
                {virtualizer.getVirtualItems().map((vItem) => {
                  const tagOrMember = PLTagOrRoomMember[vItem.index];
                  if (!tagOrMember) return null;
                  if (!('userId' in tagOrMember)) {
                    return (
                      <Text
                        style={{
                          transform: `translateY(${vItem.start}px)`,
                        }}
                        data-index={vItem.index}
                        ref={virtualizer.measureElement}
                        key={`${room.roomId}-${vItem.index}`}
                        className={classNames(css.MembersGroupLabel, css.DrawerVirtualItem)}
                        size="L400"
                        truncate={hideText}
                      >
                        {tagOrMember.name}
                      </Text>
                    );
                  }

                  return (
                    <div
                      style={{
                        transform: `translateY(${vItem.start}px)`,
                      }}
                      className={css.DrawerVirtualItem}
                      data-index={vItem.index}
                      key={`${room.roomId}-${tagOrMember.userId}`}
                      ref={virtualizer.measureElement}
                    >
                      <MemberItem
                        mx={mx}
                        useAuthentication={useAuthentication}
                        room={room}
                        member={tagOrMember}
                        onClick={handleMemberClick}
                        pressed={openProfileUserId === tagOrMember.userId}
                        typing={typingMembers.some(
                          (receipt) => receipt.userId === tagOrMember.userId
                        )}
                        hideText={hideText}
                      />
                    </div>
                  );
                })}
              </div>
            </Box>

            {fetchingMembers && (
              <Box justifyContent="Center">
                <Spinner />
              </Box>
            )}
          </Box>
        </Scroll>
      </Box>
    </Box>
  );
}
